import React, { useEffect, useMemo, useState } from 'react';
import { PropTypes } from 'prop-types';
import { SvgIcon } from 'common/react/components/SvgIcon';
import { WinnerCard } from '../winner-card/winner-card';
import { getTranslation } from 'common/react/utils/translations';
import { MatchCardFinalUpcoming } from '../match-cards/final/upcoming';
import { MatchCardFinalAwaiting } from '../match-cards/final/awaiting';
import {
    setActiveView,
    setFinalsStep,
    setSeasonStep,
    setToolTip,
    setTotalFinalsMatches,
    setLocalStorageFinalsName,
    setPredictionsFinals
} from 'common/store/modules/ladder-predictor/init';

import { useDispatch, useSelector } from 'react-redux';
import { selectCutoffs, selectLadder } from '../../selector/ladder';
import {
    createBracket,
    createSemiFinals,
    createPreliminaryFinals,
    createGrandFinal,
    createWinner,
    getTeams,
    createWildcardRound,
    WILDCARD_ROUND,
    FINALS_WEEK_ONE_ROUND,
    SEMI_FINALS_ROUND,
    PRELIMINARY_FINALS_ROUND,
    GRAND_FINAL_ROUND
} from '../../utils/generate-bracket';
import { predictionsFinalsSelector } from '../../selector/predictor';
import { useGetMatchesQuery } from 'common/store/modules/api/afl-api';
import { CONFIG } from '../constants';
import { TooltipButton } from 'common/react/components/tooltip-button';
import { useAutoScroll } from '../../hooks/use-auto-scroll';

export const Bracket = ({ seasonId, competitionId }) => {
    const [wildcardMatches, setWildcardMatches] = useState([]);
    const [qualifyingFinalsMatches, setQualifiyingFinalsMatches] = useState([]);
    const [semiFinalsMatches, setSemiFinalMatches] = useState([]);
    const [preliminaryFinalsMatches, setPreliminaryFinalMatches] = useState([]);
    const [grandFinal, setGrandFinal] = useState({});
    const [winner, setWinner] = useState({});

    const cutOffPositions = useSelector(selectCutoffs);

    const max = useSelector(
        (state) => state.ladderPredictor.progressBar.totalMatches
    );

    const current = useSelector(
        (state) => state.ladderPredictor.predictions.length
    );

    const userClosed = useSelector(
        (state) => state.ladderPredictor.progressSteps.toolTip.userClosed
    );

    const dispatch = useDispatch();
    const ladder = useSelector(selectLadder);
    const bracket = useMemo(() => {
        return getTeams(
            ladder,
            cutOffPositions.secondCutOff
                ? cutOffPositions.secondCutOff
                : cutOffPositions.firstCutOff
        );
    }, [ladder, cutOffPositions?.secondCutOff, cutOffPositions?.firstCutOff]);

    const predictions = useSelector((state) =>
        predictionsFinalsSelector(state)
    );

    const { containerRef, addToRefs } = useAutoScroll({
        qualifyingFinalsMatches,
        semiFinalsMatches,
        preliminaryFinalsMatches,
        grandFinal,
        predictions
    });

    const { data: allMatches = [] } = useGetMatchesQuery({
        pageSize: CONFIG.MAX_MATCHES,
        competitionId: competitionId,
        compSeasonId: seasonId
    });

    const renderFullFinals =
        useSelector(
            (state) => state.ladderPredictor.progressBar.totalMatches
        ) === useSelector((state) => state.ladderPredictor.predictions.length);

    const filteredFinalsMatches = useMemo(() => {
        const finalsMatches = {
            wildcardMatches: [],
            qualifyingFinalsMatches: [],
            semiFinals: [],
            preliminaryFinals: [],
            grandFinal: []
        };

        let totalMatches = [];

        for (let i = allMatches.length - 1; i >= 0; i--) {
            const match = allMatches[i];

            if (match.round.name === 'Wildcard Round') {
                finalsMatches.wildcardMatches.push(match);
            } else if (match.round.name === 'Finals Week 1') {
                finalsMatches.qualifyingFinalsMatches.push(match);
            } else if (match.round.name === 'Semi Finals') {
                finalsMatches.semiFinals.push(match);
            } else if (match.round.name === 'Preliminary Finals') {
                finalsMatches.preliminaryFinals.push(match);
            } else if (match.round.name === 'Grand Final') {
                finalsMatches.grandFinal.push(match);
            } else {
                break;
            }
            totalMatches.push(match);
        }

        // Count total matches that are concluded
        const totalMatchesLength = totalMatches.filter(
            (match) => match.status === CONFIG.CONCLUDED
        ).length;

        dispatch(
            setTotalFinalsMatches(
                (finalsMatches.wildcardMatches.length > 0
                    ? CONFIG.FINALS_MATCHES_WILDCARD
                    : CONFIG.FINALS_MATCHES) - totalMatchesLength
            )
        );

        // Set local storage once here.
        let storageName =
            'comp' +
            competitionId +
            '_season' +
            seasonId +
            '_' +
            CONFIG.LOCAL_STORAGE_NAME_BRACKET;

        dispatch(setLocalStorageFinalsName(storageName));

        const storedPredictions = JSON.parse(localStorage.getItem(storageName));

        if (storedPredictions?.length) {
            dispatch(setPredictionsFinals(storedPredictions));
        }

        return finalsMatches;
    }, [bracket]);

    useEffect(() => {
        if (cutOffPositions.secondCutOff) {
            setWildcardMatches(createWildcardRound(bracket, cutOffPositions));
        }

        setQualifiyingFinalsMatches(
            createBracket(ladder, cutOffPositions, predictions)
        );

        setSemiFinalMatches(createSemiFinals(predictions, bracket));

        setPreliminaryFinalMatches(
            createPreliminaryFinals(predictions, bracket)
        );

        setGrandFinal(createGrandFinal(predictions, bracket));

        setWinner(createWinner(predictions, bracket));
    }, [predictions, filteredFinalsMatches]);

    // Get team type from teams
    const teamType = useMemo(() => {
        let type = 'MEN';
        if (bracket) {
            type = bracket[0]?.team?.teamType;
        }
        return type;
    }, [bracket]);

    return (
        <div className="ladder-predictor-bracket">
            <div className="ladder-predictor-bracket__header">
                <h3 className="ladder-predictor-bracket__header-title">
                    {getTranslation('label.ladderPredictor.finalsPredictions')}
                </h3>
                {!renderFullFinals ? (
                    <span className="ladder-predictor-bracket__header-info">
                        <SvgIcon
                            icon="info-circle"
                            className="icon ladder-predictor-bracket__header-info-icon"
                        />
                        {getTranslation('label.ladderPredictor.emptyFinals')}
                    </span>
                ) : null}
            </div>

            <div
                className="ladder-predictor-bracket__bracket"
                ref={containerRef}
            >
                {wildcardMatches.length ? (
                    <div
                        className="ladder-predictor-bracket__bracket-col ladder-predictor-bracket__bracket-col--wildcard"
                        ref={addToRefs}
                        data-round={WILDCARD_ROUND}
                    >
                        <div className="ladder-predictor-bracket__bracket-col-title ladder-predictor-bracket__bracket-col-title--with-tooltip">
                            {getTranslation(
                                'label.ladderPredictor.columns.wildcard'
                            )}
                            <TooltipButton
                                id={`ladder-predictor-wildcard-tooltip`}
                                primaryTooltipContent={
                                    <div className="ladder-predictor-bracket__tooltip-content">
                                        <h3 className="ladder-predictor-bracket__tooltip-heading">
                                            {getTranslation(
                                                'label.ladderPredictor.wildcardTooltipTitle'
                                            )}
                                        </h3>
                                        <p className="ladder-predictor-bracket__tooltip-content">
                                            {getTranslation(
                                                'label.ladderPredictor.wildcardTooltipContent'
                                            )}
                                        </p>
                                        <a
                                            className="ladder-predictor-bracket__tooltip-link"
                                            href={getTranslation(
                                                'label.ladderPredictor.wildcardTooltip.readMore.link'
                                            )}
                                        >
                                            {getTranslation(
                                                'label.ladderPredictor.wildcardTooltip.readMore.label'
                                            )}
                                        </a>
                                    </div>
                                }
                                direction="below"
                                classNames={{
                                    tooltip:
                                        'ladder-predictor-bracket__tooltip-wrapper'
                                }}
                            >
                                <SvgIcon
                                    icon="help-circle"
                                    className="ladder-predictor-bracket__tooltip-icon icon"
                                />
                            </TooltipButton>
                        </div>
                        <div className="ladder-predictor-bracket__bracket-col-cards">
                            {wildcardMatches?.map((match, i) =>
                                renderFullFinals ? (
                                    <MatchCardFinalUpcoming
                                        match={match}
                                        key={i}
                                        matchesData={
                                            filteredFinalsMatches.wildcardMatches
                                        }
                                        seasonId={seasonId}
                                        competitionId={competitionId}
                                        finalsStage="wildcard"
                                    />
                                ) : (
                                    <MatchCardFinalAwaiting
                                        key={i}
                                        finalsStage="wildcard"
                                        index={i}
                                        hasWildcard={true}
                                    />
                                )
                            )}
                        </div>
                    </div>
                ) : null}

                <div
                    className="ladder-predictor-bracket__bracket-col"
                    ref={addToRefs}
                    data-round={FINALS_WEEK_ONE_ROUND}
                >
                    <p className="ladder-predictor-bracket__bracket-col-title">
                        {getTranslation(
                            'label.ladderPredictor.columns.qualifyingFinals'
                        )}
                    </p>
                    <div className="ladder-predictor-bracket__bracket-col-cards">
                        {qualifyingFinalsMatches?.map((match, i) =>
                            renderFullFinals ? (
                                <MatchCardFinalUpcoming
                                    match={match}
                                    key={i}
                                    matchesData={
                                        filteredFinalsMatches.qualifyingFinalsMatches
                                    }
                                    seasonId={seasonId}
                                    competitionId={competitionId}
                                    finalsStage="qualifying"
                                    index={i}
                                />
                            ) : (
                                <MatchCardFinalAwaiting
                                    key={i}
                                    finalsStage="qualifying"
                                    index={i}
                                    hasWildcard={wildcardMatches.length > 0}
                                />
                            )
                        )}
                    </div>
                </div>

                <div
                    className="ladder-predictor-bracket__bracket-col ladder-predictor-bracket__bracket-col--semi-finals"
                    ref={addToRefs}
                    data-round={SEMI_FINALS_ROUND}
                >
                    <p className="ladder-predictor-bracket__bracket-col-title">
                        {getTranslation(
                            'label.ladderPredictor.columns.semiFinals'
                        )}
                    </p>
                    <div className="ladder-predictor-bracket__bracket-col-descriptor ladder-predictor-bracket__bracket-col-descriptor--top">
                        {getTranslation('label.ladderPredictor.columns.l')}
                    </div>
                    <div className="ladder-predictor-bracket__bracket-col-descriptor ladder-predictor-bracket__bracket-col-descriptor--bottom">
                        {getTranslation('label.ladderPredictor.columns.l')}
                    </div>
                    <div className="ladder-predictor-bracket__bracket-col-cards">
                        {semiFinalsMatches?.map((match, i) =>
                            match.home || match.away ? (
                                <MatchCardFinalUpcoming
                                    match={match}
                                    key={i}
                                    finalsStage="semiFinal"
                                    index={i}
                                    matchesData={
                                        filteredFinalsMatches.semiFinals
                                    }
                                    seasonId={seasonId}
                                    competitionId={competitionId}
                                />
                            ) : (
                                <MatchCardFinalAwaiting
                                    key={i}
                                    finalsStage="semiFinal"
                                    index={i}
                                />
                            )
                        )}
                    </div>
                </div>

                <div
                    className="ladder-predictor-bracket__bracket-col ladder-predictor-bracket__bracket-col--preliminary-finals"
                    ref={addToRefs}
                    data-round={PRELIMINARY_FINALS_ROUND}
                >
                    <p className="ladder-predictor-bracket__bracket-col-title">
                        {getTranslation(
                            'label.ladderPredictor.columns.preliminaryFinals'
                        )}
                    </p>
                    <div className="ladder-predictor-bracket__bracket-col-descriptor ladder-predictor-bracket__bracket-col-descriptor--top">
                        {getTranslation('label.ladderPredictor.columns.w')}
                    </div>
                    <div className="ladder-predictor-bracket__bracket-col-descriptor ladder-predictor-bracket__bracket-col-descriptor--bottom">
                        {getTranslation('label.ladderPredictor.columns.w')}
                    </div>
                    <div className="ladder-predictor-bracket__bracket-col-cards">
                        {preliminaryFinalsMatches?.map((match, i) =>
                            match.home || match.away ? (
                                <MatchCardFinalUpcoming
                                    match={match}
                                    key={i}
                                    finalsStage="preliminary"
                                    index={i}
                                    matchesData={
                                        filteredFinalsMatches.preliminaryFinals
                                    }
                                    seasonId={seasonId}
                                    competitionId={competitionId}
                                />
                            ) : (
                                <MatchCardFinalAwaiting
                                    key={i}
                                    finalsStage="preliminary"
                                    index={i}
                                />
                            )
                        )}
                    </div>
                </div>

                <div
                    className="ladder-predictor-bracket__bracket-col ladder-predictor-bracket__bracket-col--grand-final"
                    ref={addToRefs}
                    data-round={GRAND_FINAL_ROUND}
                >
                    <p className="ladder-predictor-bracket__bracket-col-title">
                        {getTranslation(
                            'label.ladderPredictor.columns.grandFinal'
                        )}
                    </p>
                    {winner ? (
                        <WinnerCard
                            teamName={winner?.team?.name}
                            providerId={winner?.team?.providerId}
                            teamType={teamType}
                        />
                    ) : (
                        <WinnerCard
                            teamName={''}
                            providerId={''}
                            teamType={teamType}
                        />
                    )}

                    <div className="ladder-predictor-bracket__bracket-col-cards">
                        {grandFinal?.home || grandFinal?.away ? (
                            <MatchCardFinalUpcoming
                                match={grandFinal}
                                finalsStage="grandFinal"
                                matchesData={filteredFinalsMatches.grandFinal}
                                seasonId={seasonId}
                                competitionId={competitionId}
                            />
                        ) : (
                            <MatchCardFinalAwaiting finalsStage="grandFinal" />
                        )}
                    </div>
                </div>
            </div>

            <div className="ladder-predictor-bracket__footer">
                <button
                    className="ladder-predictor-bracket__footer-button"
                    onClick={() => {
                        dispatch(setActiveView(CONFIG.SEASON));
                        dispatch(
                            setToolTip({
                                open: current === max && userClosed === false,
                                userClosed: userClosed
                            })
                        );
                        dispatch(
                            setFinalsStep({
                                active: true,
                                current: false,
                                complete: false
                            })
                        );
                        dispatch(
                            setSeasonStep({
                                active: true,
                                current: true,
                                complete: false
                            })
                        );
                    }}
                >
                    <SvgIcon
                        icon="arrow-left"
                        className="ladder-predictor-bracket__footer-button-icon icon"
                    />
                    {getTranslation('label.ladderPredictor.editSeason')}
                </button>
            </div>
        </div>
    );
};

Bracket.propTypes = {
    seasonId: PropTypes.string,
    competitionId: PropTypes.string
};
